import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { BookOpen, ChevronLeft, ChevronRight, X } from "lucide-react";

export const GalleryLightbox = ({ items, index, onClose, onIndex, stories = {}, storyFirst = false }) => {
  const touchX = useRef(null);
  const open = index !== null && items[index];
  const [showStory, setShowStory] = useState(false);
  const story = open && stories[open.src];
  useEffect(() => { setShowStory(storyFirst); }, [index, storyFirst]);
  const go = (d) => onIndex((index + d + items.length) % items.length);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  });

  const onTouchEnd = (e) => {
    const dx = e.changedTouches[0].clientX - (touchX.current ?? 0);
    if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-md"
          onClick={onClose} onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }} onTouchEnd={onTouchEnd}
          data-testid="gallery-lightbox"
        >
          <button onClick={onClose} aria-label="Close" className="absolute right-4 top-4 rounded-full bg-white/10 p-2.5 text-white transition-colors hover:bg-white/20" data-testid="lightbox-close-btn"><X size={22} /></button>
          {items.length > 1 && <>
          <button onClick={(e) => { e.stopPropagation(); go(-1); }} aria-label="Previous" className="absolute left-3 rounded-full bg-white/10 p-3 text-white transition-colors hover:bg-primary hover:text-black sm:left-6" data-testid="lightbox-prev-btn"><ChevronLeft size={24} /></button>
          <button onClick={(e) => { e.stopPropagation(); go(1); }} aria-label="Next" className="absolute right-3 rounded-full bg-white/10 p-3 text-white transition-colors hover:bg-primary hover:text-black sm:right-6" data-testid="lightbox-next-btn"><ChevronRight size={24} /></button>
          </>}
          <motion.figure key={open.src} initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="flex max-w-[88vw] flex-col items-center" onClick={(e) => e.stopPropagation()}>
            <img src={open.src} alt={open.caption} className={`${showStory ? "max-h-[45vh]" : "max-h-[78vh]"} max-w-full rounded-2xl object-contain shadow-2xl`} data-testid="lightbox-image" />
            <figcaption className="mt-4 text-center">
              <p className="text-sm font-extrabold uppercase tracking-wide text-white" data-testid="lightbox-name">{open.name}{open.sub && <span className="block">{open.sub}</span>}</p>
              <p className="mt-1 font-serif text-lg text-white" data-testid="lightbox-caption">{open.caption}</p>
              {story && <button type="button" onClick={() => setShowStory((v) => !v)} data-testid="lightbox-story-toggle"
                className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-1.5 text-xs font-bold text-primary-foreground"><BookOpen size={13} /> {showStory ? "Hide story" : "Tap to read story"}</button>}
              {story && showStory && <p className="mx-auto mt-3 max-h-[30vh] max-w-xl overflow-y-auto whitespace-pre-line rounded-2xl bg-white/10 p-4 text-left text-sm leading-relaxed text-white" data-testid="lightbox-story">{story}</p>}
              <p className="mt-1 font-mono text-[11px] uppercase tracking-widest text-primary" data-testid="lightbox-counter">{open.category}{items.length > 1 && ` · ${index + 1} / ${items.length}`}</p>
            </figcaption>
          </motion.figure>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
